import {useRef, useState} from "react";
import Link from "next/link";
import Image from "next/image";
import { BiChevronLeft, BiChevronRight} from "react-icons/bi";

export default function HorizontalCardSlider({
    items = [],
    cardWidth = "300px",
    cardHeight = "400px",
    imageHeight = "200px",
    className = "",
    showArrows = true,
    scrollAmount = 300,
    // Category props
    showCategories = false,
    listCate = [],
    itemsByCategory = null,
    defaultCategory = 'all',
}) {
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [activeCategory, setActiveCategory] = useState(defaultCategory);

    // Determine which items to display
    const displayItems = showCategories && itemsByCategory
        ? (itemsByCategory[activeCategory] || [])
        : items;

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollValue = direction === 'left' ? -scrollAmount : scrollAmount;
            scrollContainerRef.current.scrollBy({
                left: scrollValue,
                behavior: 'smooth'
            });
        }
    };

    const CardItem = ({item}) => {
        let customLink = item.link;
        if (customLink?.indexOf('/bai-viet/') > -1) {
            customLink = `/${customLink}`;
        } else {
            customLink = `/bai-viet/${item?.categoryCode}/${item.link}`;
        }

        return (
            <Link href={customLink} className="group block flex-shrink-0" style={{width: cardWidth}}>
                <div
                    className="hover:shadow-md hover:shadow-primary transition-shadow overflow-hidden h-full">
                    {/* Image */}
                    <div className="relative overflow-hidden aspect-video">
                        <Image
                            src={item.photo || item.image}
                            alt={item.title}
                            fill
                            className="group-hover:scale-105 transition-transform duration-300"
                        />
                    </div>

                    {/* Content */}
                    <div className="p-4 pb-0">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-3 group-hover:text-primary transition-colors leading-snug h-[74px]">
                            {item.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed h-[68.5px]">
                            {item.excerpt || item.metaDescription}
                        </p>
                    </div>
                </div>
            </Link>
        )
    };

    return (
        <div className={`w-full ${className} bg-white`}>
            {/* Category Tabs (Optional) */}
            {showCategories && (
                <div className="flex flex-wrap gap-3 mb-6">
                    {listCate.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setActiveCategory(category.id)}
                            className={`px-6 py-3 rounded-full font-medium transition-colors ${activeCategory === category.id
                                ? "bg-primary text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            {category.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Slider Container with Arrows */}
            <div className="relative group/slider">
                {/* Left Arrow */}
                {showArrows && showLeftArrow && (
                    <button
                        onClick={() => scroll('left')}
                        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300"
                        aria-label="Scroll left"
                    >
                        <BiChevronLeft className="text-2xl text-gray-700"/>
                    </button>
                )}

                {/* Right Arrow */}
                {showArrows && showRightArrow && (
                    <button
                        onClick={() => scroll('right')}
                        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300"
                        aria-label="Scroll right"
                    >
                        <BiChevronRight className="text-2xl text-gray-700"/>
                    </button>
                )}

                {/* Horizontal Scrollable Cards */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="overflow-x-auto overflow-y-hidden scrollbar-hide"
                >
                    <div className={`flex gap-4`}>
                        {displayItems.map((item) => (
                            <CardItem key={item.id} item={item}/>
                        ))}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }

                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
}
